/**
 * Deterministic stub SwimFirehoseResponse modeled on Scott's May 9 swim shape:
 * 2050m, 56 min, ~82 lengths in a 25m pool, mixed-stroke main set.
 *
 * Used when the page is loaded with `?firehoseStub=1` — gives the desktop browser
 * a realistic fixture for visual verification (Playwright screenshots, design QA)
 * without needing the WKWebView bridge.
 *
 * Workout structure (82 lengths):
 *   L1-16   Free warm-up
 *   L17-24  Drill set (2 fly, 2 back, 2 breast, 2 free)
 *   L25-56  Main set: 8 × 100 IM (fly/back/breast/free × 8)
 *   L57-72  Tempo free
 *   L73-82  Free cool-down
 *
 * HR profile rises → peaks in main set → cools down. Stroke counts vary per
 * stroke with fatigue. Numbers are illustrative, not pulled from real samples.
 */

import type {
  SwimFirehoseResponse,
  FirehoseSampleEntry,
  FirehoseTypeBucket,
  FirehoseWorkoutEvent,
} from "@contract/types";
import { debugSearch } from "./debugSearch";

const START_MS = new Date("2026-05-09T13:38:00Z").getTime();
const LENGTH_METERS = 25;
const LENGTH_COUNT = 82;

/** Strokes 0..5 mapped from HKSwimmingStrokeStyle. */
const STROKE_FREE = 2;
const STROKE_BACK = 3;
const STROKE_BREAST = 4;
const STROKE_FLY = 5;

interface PlannedLap {
  stroke: number;
  durationSec: number;
  spl: number;
  hrFloor: number;
  hrCeiling: number;
  /** Seconds of REST that follow this lap (before the next lap starts).
   *  Lets the stub mirror real workouts with conversation breaks + set rests. */
  restAfterSec?: number;
}

function plan(): PlannedLap[] {
  const out: PlannedLap[] = [];
  // L1-14: Warm-up. First lap Breast, then Free. Ends with the conversation break.
  out.push({ stroke: STROKE_BREAST, durationSec: 31, spl: 16, hrFloor: 90, hrCeiling: 110 });
  for (let i = 0; i < 12; i++) {
    out.push({ stroke: STROKE_FREE, durationSec: 22 + (i % 3), spl: 13 + (i % 2), hrFloor: 110, hrCeiling: 138 });
  }
  // L14 — final warm-up lap, then long conversation break
  out.push({ stroke: STROKE_FREE, durationSec: 26, spl: 13, hrFloor: 135, hrCeiling: 145, restAfterSec: 262 });

  // L15-56: Main block — Free with Breast/Back/Fly intermixed (Scott's pattern)
  // Sparse rests inside the block to mimic real Apple Watch behavior
  const mainBlockStrokeCycle = [
    STROKE_FREE, STROKE_FREE, STROKE_FREE, STROKE_FREE, STROKE_FREE, STROKE_FREE, STROKE_FREE,
    STROKE_BREAST,
    STROKE_FREE, STROKE_FREE, STROKE_FREE, STROKE_FREE, STROKE_FREE,
    STROKE_BREAST,
    STROKE_FREE, STROKE_FREE, STROKE_FREE,
    STROKE_BREAST, STROKE_BACK,
    STROKE_FREE, STROKE_FREE, STROKE_FREE,
    STROKE_BREAST,
    STROKE_FREE, STROKE_FREE, STROKE_FREE, STROKE_FREE, STROKE_FREE,
    STROKE_BREAST,
    STROKE_FREE,
    STROKE_BREAST,
    STROKE_FREE, STROKE_FREE, STROKE_FREE,
    STROKE_BREAST, STROKE_BACK, STROKE_BACK, STROKE_BACK, STROKE_BACK,
    STROKE_BREAST, STROKE_BACK,
  ];
  mainBlockStrokeCycle.slice(0, 40).forEach((s, i) => {
    const dur = s === STROKE_BREAST ? 36 + (i % 4) : s === STROKE_BACK ? 35 + (i % 3) : 28 + (i % 4);
    const spl = s === STROKE_BREAST ? 9 : s === STROKE_BACK ? 14 : 14 + (i % 2);
    out.push({
      stroke: s,
      durationSec: dur,
      spl,
      hrFloor: 120 + Math.floor(i / 6),
      hrCeiling: 145 + Math.floor(i / 8),
    });
  });
  // After the main block, a short 50m mini-set (2 lengths, including a fly)
  // then a 2-minute rest before the 3×200 finisher
  out[out.length - 1].restAfterSec = 0;
  out.push({ stroke: STROKE_FLY, durationSec: 38, spl: 20, hrFloor: 138, hrCeiling: 158 });
  out.push({ stroke: STROKE_FLY, durationSec: 41, spl: 19, hrFloor: 140, hrCeiling: 158, restAfterSec: 123 });

  // Set 1 of 3 (8 lengths, 200m) — Free/Free/Fly/Free/Free/Free/Free/Fly
  const set1Strokes = [STROKE_FREE, STROKE_FREE, STROKE_FLY, STROKE_FREE, STROKE_FREE, STROKE_FREE, STROKE_FREE, STROKE_FLY];
  set1Strokes.forEach((s, i) => {
    const dur = s === STROKE_FLY ? 36 : 28;
    const spl = s === STROKE_FLY ? 18 : 14;
    out.push({
      stroke: s,
      durationSec: dur,
      spl,
      hrFloor: 110 + i * 2,
      hrCeiling: 128 + i * 2,
    });
  });
  out[out.length - 1].restAfterSec = 62;

  // Set 2 of 3 (8 lengths, 200m) — starts with FLY (Scott's silk Fly was here)
  const set2Strokes = [STROKE_FLY, STROKE_FREE, STROKE_BACK, STROKE_FREE, STROKE_FLY, STROKE_FREE, STROKE_FLY, STROKE_FREE];
  set2Strokes.forEach((s, i) => {
    const dur = s === STROKE_FLY ? 22 + i : s === STROKE_BACK ? 32 : 28;
    const spl = s === STROKE_FLY ? 11 + Math.floor(i / 4) : s === STROKE_BACK ? 13 : 15;
    out.push({
      stroke: s,
      durationSec: dur,
      spl,
      hrFloor: 100 + i * 6,
      hrCeiling: 120 + i * 5,
    });
  });
  out[out.length - 1].restAfterSec = 48;

  // Set 3 of 3 (10 lengths, 250m — the strong finish)
  const set3Strokes = [STROKE_BACK, STROKE_FLY, STROKE_FREE, STROKE_FREE, STROKE_BREAST, STROKE_BACK, STROKE_FLY, STROKE_BACK, STROKE_FLY, STROKE_FREE];
  set3Strokes.forEach((s, i) => {
    const dur = s === STROKE_FLY ? 36 : s === STROKE_BACK ? 35 : s === STROKE_BREAST ? 36 : 30;
    const spl = s === STROKE_FLY ? 19 : s === STROKE_BACK ? 13 : s === STROKE_BREAST ? 16 : 14;
    out.push({
      stroke: s,
      durationSec: dur,
      spl,
      hrFloor: 130 + i * 3,
      hrCeiling: 148 + i * 2,
    });
  });
  return out;
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

export function generateStubFirehose(): SwimFirehoseResponse {
  const lapPlan = plan();
  if (lapPlan.length !== LENGTH_COUNT) {
    // defensive — keep the visual fixture consistent
    throw new Error(`stub plan produced ${lapPlan.length} lengths, expected ${LENGTH_COUNT}`);
  }

  // Build lap events + per-second HR samples + per-lap stroke count samples
  const events: FirehoseWorkoutEvent[] = [];
  const hrSamples: FirehoseSampleEntry[] = [];
  const strokeCountSamples: FirehoseSampleEntry[] = [];
  const distanceSamples: FirehoseSampleEntry[] = [];

  let cursorMs = START_MS;
  let totalDurationSec = 0;

  for (let i = 0; i < lapPlan.length; i++) {
    const lap = lapPlan[i];
    const lapStartMs = cursorMs;
    const lapEndMs = lapStartMs + lap.durationSec * 1000;
    totalDurationSec += lap.durationSec;

    // Lap event. HKWorkoutEvent convention: `date` is the START of the lap,
    // not the end. `durationSeconds` is the lap length.
    events.push({
      type: "lap",
      date: iso(lapStartMs),
      durationSeconds: lap.durationSec,
      metadata: {
        // Use the real-runtime key names Apple HealthKit actually emits — without
        // the "HKMetadataKey" prefix. Aligns the visual harness with what arrives
        // from the iOS bridge.
        HKSwimmingStrokeStyle: lap.stroke,
        HKLapLength: `${LENGTH_METERS} m`,
      },
    });

    // HR samples: one every 2 seconds inside the lap, deterministic sawtooth
    const ticks = Math.floor(lap.durationSec / 2);
    for (let t = 0; t < ticks; t++) {
      const tMs = lapStartMs + t * 2000;
      // Deterministic noise: phase based on lap+t
      const noise = ((i * 37 + t * 13) % 11) - 5;
      const progress = t / ticks;
      const bpm = Math.round(lap.hrFloor + (lap.hrCeiling - lap.hrFloor) * progress + noise * 0.6);
      hrSamples.push({
        startDate: iso(tMs),
        endDate: iso(tMs),
        value: bpm,
        sourceName: "Apple Watch",
      });
    }

    // Stroke count sample at lap end (per length)
    strokeCountSamples.push({
      startDate: iso(lapStartMs),
      endDate: iso(lapEndMs),
      value: lap.spl,
      sourceName: "Apple Watch",
    });

    // Distance sample (one per length = 25m)
    distanceSamples.push({
      startDate: iso(lapStartMs),
      endDate: iso(lapEndMs),
      value: LENGTH_METERS,
      sourceName: "Apple Watch",
    });

    cursorMs = lapEndMs;
    // Add a few HR samples DURING the rest (sparse, decaying) so detection works
    if (lap.restAfterSec && lap.restAfterSec > 20) {
      const restTicks = Math.min(8, Math.floor(lap.restAfterSec / 15));
      for (let t = 1; t <= restTicks; t++) {
        const restPct = t / (restTicks + 1);
        const restBpm = Math.round(lap.hrCeiling - (lap.hrCeiling - 88) * Math.min(1, restPct * 2));
        hrSamples.push({
          startDate: iso(cursorMs + t * 15_000),
          endDate: iso(cursorMs + t * 15_000),
          value: restBpm,
          sourceName: "Apple Watch",
        });
      }
      cursorMs += lap.restAfterSec * 1000;
    }
  }

  const workoutEndMs = cursorMs;
  const totalDistanceM = LENGTH_COUNT * LENGTH_METERS;

  function bucket(typeName: string, unit: string | null, samples: FirehoseSampleEntry[]): FirehoseTypeBucket {
    return {
      typeName,
      category: "quantity",
      unit,
      sampleCount: samples.length,
      samples,
      error: null,
    };
  }

  const buckets: FirehoseTypeBucket[] = [
    bucket("HKQuantityTypeIdentifierHeartRate", "count/min", hrSamples),
    bucket("HKQuantityTypeIdentifierSwimmingStrokeCount", "count", strokeCountSamples),
    bucket("HKQuantityTypeIdentifierDistanceSwimming", "m", distanceSamples),
    bucket("HKQuantityTypeIdentifierActiveEnergyBurned", "kcal", []),
    bucket("HKQuantityTypeIdentifierVO2Max", "mL/(kg·min)", []),
    bucket("HKQuantityTypeIdentifierRespiratoryRate", "count/min", []),
  ];

  return {
    workout: {
      uuid: "STUB-MAY9-2050M-SWIM",
      startDate: iso(START_MS),
      endDate: iso(workoutEndMs),
      activityType: "swimming",
      durationSeconds: totalDurationSec,
      totalDistance: { value: totalDistanceM, unit: "m" },
    },
    events,
    activities: [],
    buckets,
  };
}

/**
 * DETERMINISTIC per-lap pace factor for the companion stub, seeded by lap index.
 * No Math.random — same output every call. Produces a mix in roughly the 0.92×–1.08×
 * band so the Lap Race view shows You ahead on some laps, the companion ahead on
 * others, and at least one true tie.
 *
 * Design:
 *   - A sine wave over the lap index gives a smooth alternating faster/slower pattern
 *     (so there are visible RUNS where the companion is faster, not just alternating
 *     single laps).
 *   - Two indices are pinned: one exact tie (factor 1.00) and one anchor inside an
 *     explicitly-faster run, guaranteeing the QA states the view needs to exercise.
 *   - Amplitude is wide enough (±0.16) that a typical ~25-40s length yields a >3s
 *     gap on most laps — clearing the Lap Race view's ~3s dead-heat floor so the
 *     view actually renders You-ahead AND companion-ahead chips, not all ties.
 *   - Factors are symmetric around 1.00 so the total companion duration stays within
 *     ~5% of base and the HR overlay below still reads sensibly.
 */
const TIE_LAP_INDEX = 6;          // 0-based — exactly 1.00× (a true dead heat)
function companionPaceFactor(lapIndex: number, lapCount: number): number {
  if (lapIndex === TIE_LAP_INDEX) return 1.0;
  // Sine over the index: amplitude 0.16 → band ~0.84×..1.16×. On a 25-40s length this
  // is a 4-6s gap, comfortably past the ~3s dead-heat floor. Phase chosen so the first
  // several laps after the warm-up trend FASTER (a visible companion-ahead run).
  const phase = (lapIndex / Math.max(1, lapCount)) * Math.PI * 6; // ~3 full cycles
  const raw = 1.0 - 0.16 * Math.sin(phase);
  // Round to 0.001 so the output is stable and JSON-identical across calls.
  return Math.round(raw * 1000) / 1000;
}

/**
 * Companion stub: a slightly different swimmer in the same session.
 *
 * Rebuilt deterministically from generateStubFirehose() with a PER-LAP pace factor
 * (companionPaceFactor) instead of a uniform 1.05× — so the Lap Race view can show
 * both swimmers winning different laps plus a dead heat. The companion's whole
 * timeline (lap events + HR / stroke-count / distance samples) is reconstructed from
 * a single monotonic cursor so elapsed ordering is always valid (start < end < next
 * start). The workout starts +15s after Scott's (a believable two-wrist button-press
 * offset). No new random calls.
 */
/**
 * Days the `stubCompanion=lastMonth` QA scenario shifts the companion's whole
 * timeline back by (UAT 2026-06-08). 12 days reproduces the owner's real phone
 * case: his swim was Jun 8, the stored companion swim was ~May 27 — DIFFERENT days,
 * so the same-session matcher pairs nothing and the Club tab must render the
 * "no lap race today" card. Default stub behaviour is the same-session offset (0
 * extra days), so this only changes when the param is passed.
 */
const LAST_MONTH_SHIFT_DAYS = 12;
const DAY_MS = 86_400_000;

export function generateStubCompanionFirehose(
  opts: { extraDaysBack?: number } = {},
): SwimFirehoseResponse {
  const base = generateStubFirehose();
  // The believable two-wrist button-press offset (+15s after Scott's), MINUS an
  // optional whole-days shift for the different-day QA scenario. extraDaysBack > 0
  // moves the companion's swim onto an earlier day so the same-session gate fails.
  const extraDaysBack = Math.max(0, opts.extraDaysBack ?? 0);
  const SHIFT_MS = 15_000 - extraDaysBack * DAY_MS;

  const baseLapEvents = base.events.filter((e) => e.type === "lap");
  const lapCount = baseLapEvents.length;
  const companionStartMs = new Date(base.workout.startDate).getTime() + SHIFT_MS;

  // For each base lap, compute the companion's scaled duration and a stable mapping
  // from base wall-clock → companion wall-clock so we can re-stamp the samples that
  // fall inside that lap. Rest gaps after a lap are preserved (unscaled) so the
  // session shape — and the HR overlay below — still reads sensibly.
  interface LapMap {
    baseStartMs: number;
    baseEndMs: number;
    compStartMs: number;
    compDurMs: number;
  }
  const lapMaps: LapMap[] = [];
  const events: FirehoseWorkoutEvent[] = [];

  let cursorMs = companionStartMs;
  for (let i = 0; i < lapCount; i++) {
    const ev = baseLapEvents[i];
    const baseStartMs = new Date(ev.date).getTime();
    const baseDurSec = ev.durationSeconds ?? 0;
    const baseEndMs = baseStartMs + baseDurSec * 1000;

    const factor = companionPaceFactor(i, lapCount);
    const compDurSec = Math.round(baseDurSec * factor * 10) / 10; // 0.1s precision
    const compStartMs = cursorMs;
    const compDurMs = Math.round(compDurSec * 1000);
    const compEndMs = compStartMs + compDurMs;

    events.push({
      ...ev,
      date: iso(compStartMs),
      durationSeconds: compDurSec,
    });

    lapMaps.push({ baseStartMs, baseEndMs, compStartMs, compDurMs });

    // Advance the cursor by this lap, then by the REST gap that followed in the base
    // timeline (the gap between this base lap's end and the next base lap's start).
    cursorMs = compEndMs;
    const nextBaseStartMs =
      i + 1 < lapCount ? new Date(baseLapEvents[i + 1].date).getTime() : baseEndMs;
    const restMs = Math.max(0, nextBaseStartMs - baseEndMs);
    cursorMs += restMs;
  }

  const companionEndMs = cursorMs;
  const companionDurationSec = Math.round((companionEndMs - companionStartMs) / 1000);

  /** Map a base wall-clock instant to the companion timeline by linear interpolation
   *  within whichever lap (or following rest gap) it falls into, so sample ordering
   *  follows the re-stamped lap events exactly. */
  function mapInstant(baseMs: number): number {
    // Before the first lap.
    if (lapMaps.length === 0) return baseMs + SHIFT_MS;
    if (baseMs <= lapMaps[0].baseStartMs) {
      return lapMaps[0].compStartMs - (lapMaps[0].baseStartMs - baseMs);
    }
    for (let i = 0; i < lapMaps.length; i++) {
      const m = lapMaps[i];
      const nextBaseStart =
        i + 1 < lapMaps.length ? lapMaps[i + 1].baseStartMs : m.baseEndMs;
      // Inside this lap's span.
      if (baseMs <= m.baseEndMs) {
        const baseSpan = Math.max(1, m.baseEndMs - m.baseStartMs);
        const frac = (baseMs - m.baseStartMs) / baseSpan;
        return Math.round(m.compStartMs + frac * m.compDurMs);
      }
      // Inside the rest gap after this lap (and before the next lap).
      if (baseMs < nextBaseStart) {
        const compRestStart = m.compStartMs + m.compDurMs;
        // Rest gaps are preserved 1:1, so just offset from the companion rest start.
        return Math.round(compRestStart + (baseMs - m.baseEndMs));
      }
    }
    // After the last lap.
    const last = lapMaps[lapMaps.length - 1];
    return Math.round(last.compStartMs + last.compDurMs + (baseMs - last.baseEndMs));
  }

  const workout: SwimFirehoseResponse["workout"] = {
    ...base.workout,
    uuid: "STUB-COMPANION-MAY9-2050M-SWIM",
    startDate: iso(companionStartMs),
    endDate: iso(companionEndMs),
    durationSeconds: companionDurationSec,
  };

  const buckets = base.buckets.map((b) => ({
    ...b,
    samples: b.samples.map((s) => ({
      ...s,
      startDate: iso(mapInstant(new Date(s.startDate).getTime())),
      endDate: iso(mapInstant(new Date(s.endDate).getTime())),
    })),
  }));

  return { workout, events, activities: [], buckets };
}

// ─── Open-water stub ──────────────────────────────────────────────────────────

/** One planned open-water split: GPS distance + duration + stroke + HR band. */
interface PlannedSplit {
  stroke: number;      // HKSwimmingStrokeStyle int, or -1 for a distance window with NO stroke partner
  meters: number;
  durationSec: number;
  strokeCount: number;
  hrFloor: number;
  hrCeiling: number;
}

/**
 * Deterministic open-water stub modeled on the founding lake swim shape:
 * paired DistanceSwimming × SwimmingStrokeCount windows (join on identical
 * start/end), a single whole-swim `segment` event, ZERO lap events, and
 * `activities[0].swimmingLocationType: "openWater"`.
 *
 * The plan includes:
 *   - a long slow "wade-in" first split (mirrors the real 163s/35.8m fly-misread
 *     window) to exercise the long-split path,
 *   - one distance window with NO stroke partner → the deriver must stamp it
 *     "Unknown" (stroke -1 below drops its stroke-count sample from the bucket).
 *
 * Numbers are illustrative, not pulled from real samples.
 */
function planOpenWater(): PlannedSplit[] {
  const out: PlannedSplit[] = [];
  // Wade-in: long, slow, few strokes, misread as fly (matches the real swim).
  out.push({ stroke: STROKE_FLY, meters: 35.8, durationSec: 163, strokeCount: 4, hrFloor: 96, hrCeiling: 120 });
  // Steady freestyle body of the swim (24 windows), ~26m / ~30s each.
  for (let i = 0; i < 24; i++) {
    out.push({
      stroke: STROKE_FREE,
      meters: 24 + (i % 5),
      durationSec: 28 + (i % 4),
      strokeCount: 18 + (i % 3),
      hrFloor: 128 + Math.floor(i / 6),
      hrCeiling: 148 + Math.floor(i / 8),
    });
  }
  // A short breaststroke stretch (3 windows).
  for (let i = 0; i < 3; i++) {
    out.push({ stroke: STROKE_BREAST, meters: 22 + i, durationSec: 34 + i, strokeCount: 12 + i, hrFloor: 132, hrCeiling: 150 });
  }
  // One distance window with NO stroke partner → Unknown after derive.
  out.push({ stroke: -1, meters: 27.5, durationSec: 31, strokeCount: 0, hrFloor: 140, hrCeiling: 152 });
  // A couple more freestyle windows to finish.
  for (let i = 0; i < 2; i++) {
    out.push({ stroke: STROKE_FREE, meters: 25 + i, durationSec: 29 + i, strokeCount: 17 + i, hrFloor: 138, hrCeiling: 150 });
  }
  return out;
}

const OPENWATER_START_MS = new Date("2026-07-10T11:35:02Z").getTime();

export function generateStubOpenWaterFirehose(): SwimFirehoseResponse {
  const splitPlan = planOpenWater();

  const distanceSamples: FirehoseSampleEntry[] = [];
  const strokeCountSamples: FirehoseSampleEntry[] = [];
  const hrSamples: FirehoseSampleEntry[] = [];

  let cursorMs = OPENWATER_START_MS;
  let totalDurationSec = 0;
  let totalDistanceM = 0;

  for (let i = 0; i < splitPlan.length; i++) {
    const s = splitPlan[i];
    const startMs = cursorMs;
    const endMs = startMs + s.durationSec * 1000;
    totalDurationSec += s.durationSec;
    totalDistanceM += s.meters;

    // Distance window — every split has one (these are the natural "splits").
    distanceSamples.push({
      startDate: iso(startMs),
      endDate: iso(endMs),
      value: s.meters,
      sourceName: "Apple Watch",
    });

    // Stroke-count window — paired on the SAME (start,end) key, UNLESS this
    // split has no stroke partner (stroke -1), which the deriver maps to Unknown.
    if (s.stroke >= 0) {
      strokeCountSamples.push({
        startDate: iso(startMs),
        endDate: iso(endMs),
        value: s.strokeCount,
        sourceName: "Apple Watch",
        metadata: { HKSwimmingStrokeStyle: s.stroke },
      });
    }

    // HR samples every 5s inside the split, deterministic sawtooth.
    const ticks = Math.max(1, Math.floor(s.durationSec / 5));
    for (let t = 0; t < ticks; t++) {
      const tMs = startMs + t * 5000;
      const noise = ((i * 31 + t * 17) % 9) - 4;
      const progress = t / ticks;
      const bpm = Math.round(s.hrFloor + (s.hrCeiling - s.hrFloor) * progress + noise * 0.5);
      hrSamples.push({ startDate: iso(tMs), endDate: iso(tMs), value: bpm, sourceName: "Apple Watch" });
    }

    cursorMs = endMs; // open water windows are contiguous (rests absorbed into windows)
  }

  const workoutEndMs = cursorMs;

  function bucket(typeName: string, unit: string | null, samples: FirehoseSampleEntry[]): FirehoseTypeBucket {
    return { typeName, category: "quantity", unit, sampleCount: samples.length, samples, error: null };
  }

  const buckets: FirehoseTypeBucket[] = [
    bucket("HKQuantityTypeIdentifierDistanceSwimming", "m", distanceSamples),
    bucket("HKQuantityTypeIdentifierSwimmingStrokeCount", "count", strokeCountSamples),
    bucket("HKQuantityTypeIdentifierHeartRate", "count/min", hrSamples),
  ];

  const startIso = iso(OPENWATER_START_MS);
  const endIso = iso(workoutEndMs);
  // Open water: ONE whole-swim segment event, ZERO lap events.
  const events: FirehoseWorkoutEvent[] = [
    { type: "segment", date: startIso, durationSeconds: totalDurationSec },
  ];

  return {
    workout: {
      uuid: "STUB-OPENWATER-LAKE-SWIM",
      startDate: startIso,
      endDate: endIso,
      activityType: "swimming",
      durationSeconds: totalDurationSec,
      totalDistance: { value: totalDistanceM, unit: "m" },
    },
    events,
    activities: [
      {
        uuid: "STUB-OPENWATER-LAKE-SWIM",
        activityType: "swimming",
        startDate: startIso,
        endDate: endIso,
        durationSeconds: totalDurationSec,
        locationType: "outdoor",
        swimmingLocationType: "openWater",
        events,
      },
    ],
    buckets,
  };
}

/** Read URL search params to decide whether the stub should activate (dev builds only). */
export function shouldUseStubFirehose(): boolean {
  return new URLSearchParams(debugSearch()).get("firehoseStub") === "1";
}

/**
 * How many extra days back to shift the stub companion's timeline, read from the
 * `stubCompanion` URL param (UAT 2026-06-08). `stubCompanion=lastMonth` returns
 * LAST_MONTH_SHIFT_DAYS (12) so the companion's swim lands on a different day from
 * the user's — reproducing the owner's real phone scenario (his Jun 8 swim vs. the
 * stored ~May 27 companion swim) so the "no lap race today" card is screenshot-able
 * in a desktop browser. Any other value (or absence) returns 0 — default stub
 * behaviour (same session) is unchanged.
 */
export function companionExtraDaysBack(): number {
  return new URLSearchParams(debugSearch()).get("stubCompanion") === "lastMonth" ? LAST_MONTH_SHIFT_DAYS : 0;
}
